import React from "react";
import { Link, Navigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import Copyright from "components/partials/auth/copyright";
import Logo from "components/partials/auth/logo";
import { forgotPasswordAction } from "action/auth-action";
import { routes } from "router";
import { useAuth } from "context/auth-context";

const schema = z.object({
  email: z.string().email("Enter a valid email address."),
});

type FormData = z.infer<typeof schema>;

export default function ForgotPassword() {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  if (isAuthenticated) return <Navigate to={routes.Home.path} replace />;

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    const response = await forgotPasswordAction({ email: data.email ?? "" });
    setLoading(false);
    if (response.status === 200) setSubmitted(true);
  };

  return (
    <main className="min-h-dvh bg-card">
      <div className="mx-auto flex min-h-dvh w-full max-w-[524px] flex-col px-7 py-10 md:px-[42px]">
        <Link to={routes.Login.path} className="inline-flex w-fit items-center gap-3">
          <Logo />
          <span className="text-lg font-semibold text-default-900">Storbolt</span>
        </Link>
        <section className="flex flex-1 flex-col justify-center">
          {submitted ? (
            <div>
              <h1 className="text-2xl font-medium text-default-900">Check your email</h1>
              <p className="mt-3 text-base leading-6 text-default-500">
                If that email belongs to a Storbolt account, we’ve sent a link to reset its password.
              </p>
              <Link
                to={routes.Login.path}
                className="mt-7 inline-block text-sm font-medium text-default-900 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-medium text-default-900">Forgot your password?</h1>
              <p className="mt-3 text-base leading-6 text-default-500">
                Enter the email address associated with your account and we’ll send you a reset link.
              </p>
              <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-medium text-default-600">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    size="lg"
                    aria-invalid={Boolean(errors.email)}
                    {...register("email")}
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
                </div>
                <Button fullWidth disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? "Sending link..." : "Send reset link"}
                </Button>
              </form>
              <Link
                to={routes.Login.path}
                className="mt-7 inline-block text-sm font-medium text-default-900 hover:underline"
              >
                Back to sign in
              </Link>
            </>
          )}
        </section>
        <footer className="pt-10 text-center text-xs font-normal text-default-500"><Copyright /></footer>
      </div>
    </main>
  );
}
